import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CALENDLY_LINK, CONTACT_PHONE, CONTACT_EMAIL } from '../../../utils/constants';

const CTASection = () => {
  const contactMethods = [
    {
      title: 'Free Consultation',
      description: 'Book a 30-minute consultation to discuss your valuation needs',
      icon: 'Calendar',
      action: 'Book Now',
      href: '/book-consultation',
      highlight: true
    },
    // {
    //   title: 'Request Proposal',
    //   description: 'Get a detailed proposal with scope, timeline, and fixed pricing',
    //   icon: 'FileText',
    //   action: 'Request Proposal',
    //   highlight: false
    // },
    {
      title: 'Call Direct',
      description: 'Speak with a valuation expert immediately',
      icon: 'Phone',
      action: CONTACT_PHONE,
      href: `tel:${CONTACT_PHONE}`,
      highlight: false
    },
    {
      title: 'Email Inquiry',
      description: 'Send us your questions and requirements',
      icon: 'Mail',
      action: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
      highlight: false
    }
  ];

  const nextSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Discuss your needs, timeline, and objectives',
      duration: '30 minutes',
      icon: 'MessageSquare'
    },
    {
      step: '02',
      title: 'Proposal & Agreement',
      description: 'Receive detailed proposal with fixed pricing',
      duration: '1-2 days',
      icon: 'FileText'
    },
    {
      step: '03',
      title: 'Information Gathering',
      description: 'Secure document collection and management interviews',
      duration: '3-5 days',
      icon: 'FolderOpen'
    },
    {
      step: '04',
      title: 'Valuation Analysis',
      description: 'Professional analysis and report preparation',
      duration: '2-3 weeks',
      icon: 'BarChart3'
    },
    {
      step: '05',
      title: 'Delivery & Presentation',
      description: 'Final report delivery and management presentation',
      duration: '1 day',
      icon: 'CheckCircle'
    }
  ];

  const urgentSupport = [
    'Priority review and analysis',
    'Fast-track documentation and delivery',
    'Support for time-critical legal matters'
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-primary via-primary/95 to-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main CTA */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-200 leading-relaxed max-w-3xl mx-auto mb-8">
            Join over 50 satisfied clients who have trusted us with their critical valuation needs.
            Let's discuss how we can support your business objectives with professional, defensible valuations.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              className="bg-white text-primary hover:bg-slate-50"
              iconName="Calendar"
              iconPosition="left"
              asChild
            >
              <Link to="/book-consultation">
                Schedule Free Consultation
              </Link>
            </Button>
            {/* <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              iconName="Download"
              iconPosition="left"
            >
              Download Sample Report
            </Button> */}
          </div>
        </div>

        {/* Contact Methods */}
        <div className="flex flex-wrap justify-center gap-6 mb-16">
          {contactMethods?.map((method, index) => (
            <a
              key={index}
              href={method?.href || '#'}
              className={`p-6 rounded-xl border transition-all duration-300 hover:transform hover:-translate-y-1 block w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.33%-1.5rem)] max-w-sm text-center ${method?.highlight
                ? 'bg-white text-primary border-white shadow-brand-lg'
                : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                }`}
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 mx-auto ${method?.highlight ? 'bg-accent text-white' : 'bg-white/20'
                }`}>
                <Icon name={method?.icon} size={24} />
              </div>

              <h3 className={`text-lg font-semibold mb-2 ${method?.highlight ? 'text-primary' : 'text-white'
                }`}>
                {method?.title}
              </h3>

              <p className={`text-sm mb-4 ${method?.highlight ? 'text-text-secondary' : 'text-slate-200'
                }`}>
                {method?.description}
              </p>

              <div className={`font-medium ${method?.highlight ? 'text-accent' : 'text-white'
                }`}>
                {method?.action}
              </div>
            </a>
          ))}
        </div>

        {/* Process Overview */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">What Happens Next?</h3>

          <div className="grid md:grid-cols-5 gap-6">
            {nextSteps?.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Icon name={step?.icon} size={24} className="text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {step?.step}
                  </div>
                </div>

                <h4 className="text-lg font-semibold text-white mb-2">{step?.title}</h4>
                <p className="text-sm text-slate-200 mb-2">{step?.description}</p>
                <div className="text-xs text-accent font-medium">{step?.duration}</div>

                {/* Connector Arrow */}
                {/* {index < nextSteps?.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 left-full w-8 transform -translate-y-1/2 -translate-x-4">
                    <Icon name="ArrowRight" size={20} className="text-white/40" />
                  </div>
                )} */}
              </div>
            ))}
          </div>
        </div>

        {/* Urgent Support */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-warning/20 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={24} className="text-warning" />
              </div>
              <h3 className="text-xl font-bold text-white">Expedited Valuation Support - Premium applies</h3>
            </div>

            <p className="text-slate-200 mb-6">
              Time-sensitive matter? We offer accelerated timelines for urgent valuation needs.            </p>

            <ul className="space-y-3 mb-6">
              {urgentSupport?.map((item, idx) => (
                <li key={idx} className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                  <span className="text-sm text-slate-200">{item}</span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              className="border-warning text-warning hover:bg-warning hover:text-white"
              iconName="Phone"
              iconPosition="left"
              fullWidth
              asChild
            >
              <a href={`tel:${CONTACT_PHONE}`}>Contact us to discuss expedited availability.</a>
            </Button>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className=" bg-success/20 rounded-lg flex items-center justify-center">
                <Icon name="Award" size={24} className="text-success" />
              </div>
              <h3 className="text-xl font-bold text-white">Why Choose Us?</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Icon name="Shield" size={16} className="text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-medium">CVA & CPA Certified Professionals</p>
                  <p className="text-sm text-slate-200">Highest industry credentials</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={16} className="text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-medium">Average 3.2 Week Delivery</p>
                  <p className="text-sm text-slate-200">Efficient, quality process</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Icon name="Heart" size={16} className="text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-medium">98% Client Satisfaction</p>
                  <p className="text-sm text-slate-200">Trusted by 50+ clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 bg-success/20 border border-success/30 rounded-full mb-6">
            <Icon name="CheckCircle" size={20} className="text-success mr-3" />
            <span className="text-success font-medium">Free Initial Consultation • No Obligation</span>
          </div>

          <p className="text-slate-200 mb-6">
            Start with a complimentary consultation to discuss your valuation needs and explore how we can help.
          </p>

          <Button
            variant="default"
            size="xl"
            className="bg-accent hover:bg-accent/90 text-white shadow-brand-lg"
            iconName="ArrowRight"
            iconPosition="right"
            asChild
          >
            <Link to="/book-consultation">
              Get Started Today
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CTASection;